<template>
  <!--
    Street, city, country, state (US only) and zip, bound to one address
    object. Errors come from the parent form: `errorFor('city')` etc., and
    `blur` reports which part was left so the form can check it.
  -->
  <div class="kb-form-fields">
    <KbField
      :field-id="`${idPrefix}-street`"
      label="Address"
      :error="errorFor?.('address')"
    >
      <InputText
        :id="`${idPrefix}-street`"
        v-model="model.address"
        size="small"
        fluid
        placeholder="Street"
        autocomplete="street-address"
        :disabled="disabled"
        :invalid="Boolean(errorFor?.('address'))"
        @blur="$emit('blur', 'address')"
      />
    </KbField>

    <div class="kb-form-pair">
      <KbField
        :field-id="`${idPrefix}-city`"
        label="City"
        :error="errorFor?.('city')"
      >
        <InputText
          :id="`${idPrefix}-city`"
          v-model="model.city"
          size="small"
          fluid
          autocomplete="address-level2"
          :disabled="disabled"
          :invalid="Boolean(errorFor?.('city'))"
          @blur="$emit('blur', 'city')"
        />
      </KbField>
      <KbField
        :field-id="`${idPrefix}-zip`"
        label="Zip"
        :error="errorFor?.('zip')"
      >
        <InputText
          :id="`${idPrefix}-zip`"
          v-model="model.zip"
          size="small"
          fluid
          autocomplete="postal-code"
          :disabled="disabled"
          :invalid="Boolean(errorFor?.('zip'))"
          @blur="$emit('blur', 'zip')"
        />
      </KbField>
    </div>

    <div class="kb-form-pair">
      <KbField
        :field-id="`${idPrefix}-country`"
        label="Country"
        :error="errorFor?.('country')"
      >
        <Select
          v-model="model.country"
          :input-id="`${idPrefix}-country`"
          :options="countries"
          option-label="label"
          option-value="value"
          filter
          auto-filter-focus
          size="small"
          fluid
          placeholder="Country"
          :disabled="disabled"
          :invalid="Boolean(errorFor?.('country'))"
          @change="onCountryChange"
        />
      </KbField>
      <KbField
        v-if="model.country === 'US'"
        :field-id="`${idPrefix}-state`"
        label="State"
        :error="errorFor?.('state')"
      >
        <Select
          v-model="model.state"
          :input-id="`${idPrefix}-state`"
          :options="states"
          option-label="label"
          option-value="value"
          filter
          auto-filter-focus
          size="small"
          fluid
          placeholder="State"
          :disabled="disabled"
          :invalid="Boolean(errorFor?.('state'))"
          @change="$emit('blur', 'state')"
        />
      </KbField>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { AddressValues } from '~/utils/formSchemas'

defineProps<{
  idPrefix: string
  errorFor?: (field: keyof AddressValues) => string | undefined
  disabled?: boolean
}>()

const emit = defineEmits<{ blur: [field: keyof AddressValues] }>()

const model = defineModel<AddressValues>({ required: true })

const { countries, states } = useLocationOptions()

// A state only applies to US addresses
function onCountryChange() {
  if (model.value.country !== 'US') model.value.state = ''
  emit('blur', 'country')
}
</script>
